import * as SelectPrimitive from '@radix-ui/react-select';
import clsx from 'clsx';
import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementRef } from 'react';

import CheckIcon from 'components/icons/new-check';

import { useInternalSelectContext } from './context';
import type { ValueType } from './types';

import style from './select-compound.module.scss';

type SelectItemProps = Omit<ComponentPropsWithoutRef<typeof SelectPrimitive.Item>, 'children' | 'value'> & {
  // The children prop must be a string because it's used directly as the select item text.
  // This is an intentional design constraint to maintain consistent UX across the application.
  // If you need to pass non-string content, please review the UX requirements as this may indicate
  // a need for a different component.
  children: string;
  // Radix Select expects the value to be a string, but we want to support both strings and numbers.
  value: ValueType;
};

const SelectItem = forwardRef<
  ElementRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(({
  className,
  children,
  value,
  ...props
}, ref) => {
  const { adapter } = useInternalSelectContext();

  return (
    <SelectPrimitive.Item
      ref={ref}
      className={clsx(style.SelectItem, className)}
      value={adapter.serialize(value)}
      {...props}
    >
      <SelectPrimitive.ItemText asChild>
        {/*
          NOTE: Do not apply styles to this <span>. When an item is selected, Radix portals
          its content into the SelectValue component, and additional styling here can
          disrupt its positioning.
          Reference: https://www.radix-ui.com/primitives/docs/components/select#itemtext
        */}
        <span>
          {children}
        </span>
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className={style.SelectIndicatorIcon} />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

export { SelectItem };
